<template>
  <div class="yacht-toast-container">
    <div id="close-button">
      <button
        aria-label="Close yacht information"
        class="button"
        @click="closeYachtInfo"
      >
        <font-awesome-icon
          aria-hidden="true"
          icon="times"
        />
      </button>
    </div>
    <div class="row">
      <div>
        <span id="club">{{ club }}</span>
        <span id="owner">{{ owner }}</span>
      </div>
    </div>
    <div class="row">
      <div>
        <span id="name">
          {{ name }}
          <a
            id="profile-link"
            :href="yachtProfileHref"
          >
            <font-awesome-icon
              icon="info-circle"
              :aria-label="`Go to ${name} yacht profile`"
            />
          </a>
        </span>
        <div
          :class="getYachtStatusClasses(yacht)"
          class="yacht-status-icon"
        />
        <span id="status">{{ status.toString() }}</span>
      </div>
    </div>
    <div class="row">
      <div>
        <span
          class="icon"
          aria-hidden="true"
        >
          <font-awesome-icon icon="tachometer-alt" />
        </span>
        <span id="speed">{{ speed }} kt</span>
      </div>
      <div>
        <span
          class="icon"
          aria-hidden="true"
        >
          <font-awesome-icon icon="compass" />
        </span>
        <span id="heading">{{ heading }}</span>
      </div>
      <div>
        <span
          class="icon"
          aria-hidden="true"
        >
          <font-awesome-icon icon="flag-checkered" />
        </span>
        <span id="dtg">{{ dtg }} nm</span>
        <span id="dtl">{{ dtl }} nm to leader</span>
      </div>
    </div>
    <div class="row yacht-toast-standings">
      <div
        v-for="standing in standings"
        :key="standing.label"
      >
        <div
          :class="getDivisionClassCode(standing.label)"
          :aria-label="`${standing.label}`"
        >
          {{ standing.label }}
          <span class="standing-place">{{ standing.place }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core'
import { faTachometerAlt, faCompass, faFlagCheckered, faTrophy, faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { EventBus } from '../../eventBus'

library.add(faTachometerAlt, faCompass, faFlagCheckered, faTrophy, faTimes, faInfoCircle)

export default {
  components: {
    FontAwesomeIcon
  },
  props: {
    yacht: {
      type: Object,
      default: () => { }
    }
  },
  computed: {
    status() {
      if (this.yacht.IsLineHonourRecord)
        return 'Record holder'

      if (this.yacht.Categories) {
        if (this.yacht.Categories.findIndex(cat => cat.Name === 'Line Honours' && cat.DivPlace === 1) > -1)
          return 'Leader (race)'
        if (this.yacht.Categories.findIndex(cat => cat.DivPlace === 1) > -1)
          return 'Leader (divison)'
      }

      if (this.yacht.Position) {
        if (this.yacht.Position.IsRetired)
          return 'Retired'
        if (this.yacht.Position.IsRacing)
          return 'Racing'
        if (this.yacht.Position.IsFinished)
          return 'Finished'
      }

      return 'Unknown'

      return status
    },
    name() {
      return this.yacht.Name
    },
    yachtProfileHref() {
      return this.yacht.CmsUrl
    },
    club() {
      return this.yacht.Club
    },
    owner() {
      return this.yacht.Owner
    },
    latitude() {
      return this.yacht.Position ? this.yacht.Position.Latitude : '-'
    },
    longitude() {
      return this.yacht.Position ? this.yacht.Position.Longitude : '-'
    },
    speed() {
      return this.yacht.Position ? this.yacht.Position.SpeedOverGround ? parseFloat(this.yacht.Position.SpeedOverGround) > 0 ?
        parseFloat(this.yacht.Position.SpeedOverGround).toFixed(1) : '-' : '-' : '-'
    },
    heading() {
      return this.yacht.Position ? this.yacht.Position.CourseOverGround ? parseInt(this.yacht.Position.CourseOverGround) > 0 ?
        parseInt(this.yacht.Position.CourseOverGround) : '-' : '-' : '-'
    },
    dtg() {
      return this.yacht.Position ? this.yacht.Position.DistanceToGo ? this.yacht.Position.DistanceToGo.toFixed(1) :
        '-' : '-'
    },
    dtl() {
      return this.yacht.Position ? this.yacht.Position.DistanceToLeader ? this.yacht.Position.DistanceToLeader.toFixed(1) : '-' : '-'
    },
    raceTime() {
      return this.yacht.RaceTimeFormat
    },
    standings() {
      const yachtStandings = []
      this.yacht.Categories.forEach(cat => {
        const catLabel = cat.Name
        const catPlace = cat.CatPlace

        if (catPlace !== 0) {
          yachtStandings.push({
            label: catLabel,
            place: catPlace
          })
        }
        else {
          yachtStandings.push({
            label: catLabel,
            place: '-'
          })
        }

        if (cat.Division) {
          if (cat.DivPlace !== 0) {
            yachtStandings.push({
              label: catLabel + ' ' + cat.Division,
              place: cat.DivPlace
            })
          }
          else {
            yachtStandings.push({
              label: catLabel + ' ' + cat.Division,
              place: '-'
            })
          }
        }
      })
      return yachtStandings
    }
  },
  methods: {
    closeYachtInfo() {
      EventBus.$emit('closeYachtInfoClicked')
    },
    getYachtStatusClasses(yacht) {
      const classNames = []

      if (yacht.IsLineHonourRecord)
        classNames.push('is-record-holder')

      if (yacht.Position) {
        if (yacht.Position.IsRetired)
          classNames.push('is-retired')
        if (yacht.Position.IsRacing)
          classNames.push('is-racing')
        if (yacht.Position.IsFinished)
          classNames.push('is-finished')
      }

      if (yacht.Categories) {
        if (yacht.Categories.findIndex(cat => cat.DivPlace === 1) > -1)
          classNames.push('is-div-leader')
        if (yacht.Categories.findIndex(cat => cat.Name === 'Line Honours' && cat.DivPlace === 1) > -1)
          classNames.push('is-race-leader')
      }

      if (classNames.length === 0)
        classNames.push('is-unknown')

      return classNames
    },
    getDivisionClassCode(catName) {
      const catNameLower = catName.toLowerCase()
      if (catNameLower.includes('line honours')) return 'div-lh'
      if (catNameLower.includes('irc')) return 'div-irc'
      if (catNameLower.includes('orci')) return 'div-orci'
      if (catNameLower.includes('phs')) return 'div-phs'
      if (catNameLower.includes('corinthian - irc')) return 'div-circ'
      if (catNameLower.includes('corinthian - phs')) return 'div-cphs'
      if (catNameLower.includes('sydney 38')) return 'div-syd38'
      return 'div-other'
    },
  }
}
</script>

<style lang="less" scoped>
.yacht-toast-container {
  background-color: white;
  border: 1px solid rgba(10, 10, 10, 0.1);
  border-radius: 5px;
  padding: 10px;
  box-shadow: 0 2px 2px 0 rgba(10, 10, 10, 0.1);
  font-family: Din_Reg, Arial;
  position: relative;
  .row {
    display: flex;
    flex-wrap: wrap;
    > *:not(:last-child) {
      margin-right: 1.5rem;
    }
    &.yacht-toast-standings {
      margin-top: 5px;
      > *:not(:last-child) {
        margin-right: 0rem;
      }
      > * {
        margin-bottom: 5px;
      }
    }
  }

  #close-button {
    position: absolute;
    top: 0;
    right: 0;
  }

  .icon {
    margin-right: 10px;
  }

  .button {
    border: 0;
    background: white;
  }

  #club,
  #owner,
  #dtl,
  #status {
    font-family: Din_Med, Arial;
    text-transform: uppercase;
    font-family: Din_Med, Arial;
    font-size: 0.75rem;
    color: rgba(10, 10, 10, 0.5);
  }

  #club::after {
    content: "·";
    margin: 5px;
  }

  #name {
    // display: block;
    font-family: Din_Reg, Arial;
    font-size: 1.5rem;
    margin-right: 10px;
  }

  #profile-link {
    color: rgba(10, 10, 10, 0.5);
    font-size: 1rem;
  }

  #dtg {
    margin-right: 10px;
  }
}
</style>

<style lang="less" scoped>
.yacht-status-icon {
  background-size: cover;
  background-repeat: no-repeat;
  width: 0.5rem;
  height: 0.75rem;
  margin: 0 5px 0 0;
  display: inline-block;
}

.is-retired {
  background-image: url(../../assets/yacht-flag-grey.png);
}

.is-racing,
.is-finished {
  background-image: url(../../assets/yacht-flag-blue.png);
}

.is-div-leader {
  background-image: url(../../assets/yacht-flag-green.png);
}

.is-race-leader {
  background-image: url(../../assets/yacht-flag-red.png);
}

.is-record-holder {
  background-image: url(../../assets/yacht-flag-orange.png);
}
</style>


<style lang="less" scoped>
[class^="div-"] {
  background-color: lightblue;
  border-radius: 10px;
  height: 20px;
  margin-right: 4px;
  font-size: 0.75rem;
  padding: 0 5px;
  box-sizing: border-box;
  vertical-align: middle;
  line-height: 1.25rem;
  font-family: Din_Med, Arial;
  .is-selected & {
    background-color: white;
    color: #1787cb;
  }
  .standing-place {
    border-radius: 10px;
    height: 20px;
    padding: 0 5px;
  }
}

.div-lh {
  background-color: rgba(54, 192, 0, 0.1);
  color: rgba(54, 192, 0, 1);
  .standing-place {
    background-color: rgba(54, 192, 0, 1);
    color: white;
  }
}

.div-irc {
  background-color: rgba(255, 0, 0, 0.1);
  color: rgba(255, 0, 0, 1);
  .standing-place {
    background-color: rgba(255, 0, 0, 1);
    color: white;
  }
}

.div-circ {
  background-color: rgba(255, 0, 0, 0.1);
  color: rgba(255, 0, 0, 1);
  .standing-place {
    background-color: rgba(255, 0, 0, 1);
    color: white;
  }
}

.div-orci {
  background-color: rgba(255, 166, 0, 0.1);
  color: rgba(255, 166, 0, 1);
  .standing-place {
    background-color: rgba(255, 166, 0, 1);
    color: white;
  }
}

.div-phs {
  background-color: rgba(0, 60, 255, 0.1);
  color: rgba(0, 60, 255, 1);
  .standing-place {
    background-color: rgba(0, 60, 255, 1);
    color: white;
  }
}

.div-cphs {
  background-color: rgba(0, 60, 255, 0.1);
  color: rgba(0, 60, 255, 1);
  .standing-place {
    background-color: rgba(0, 60, 255, 1);
    color: white;
  }
}

.div-syd38 {
  background-color: rgba(140, 0, 255, 0.1);
  color: rgb(140, 0, 255);
  .standing-place {
    background-color: rgb(140, 0, 255);
    color: white;
  }
}

.div-other {
  background-color: rgba(121, 121, 121, 0.1);
  color: rgb(121, 121, 121);
  .standing-place {
    background-color: rgb(121, 121, 121);
    color: white;
  }
}
</style>